/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendTimeFilterList,
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectTrendTimeVisuals,
  selectWeeklyTrendTimeVisuals,
  selectRevenueIsDisplayTT,
  selectOccupiedRoomsIsDisplayTT,
  selectAvgBookingWindowIsDisplayTT,
  selectAvgLosIsDisplayTT,
  selectCxlIsDisplayTT,
  selectAdrIsDisplayTT,
  selectTrendTimeCumulativeVisuals,
  selectIsPercentageChange,
  selectTrendTimePercentageVisuals,
  selectTrendHotelNormalizedView,
  selectIsMovingAverage,
  selectMovingAverageVisualTT,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { selectEmbedToken, selectTabList } from 'modules/dashboard/selectors';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import { EXPORT_OPTIONS, REPORT_TYPE } from 'modules/dashboard/constants';
import { findVisual } from 'modules/dashboard/components/tab-container/trend-tabs/functions';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import { AssistantIcon } from 'modules/common/components';
import { isEmpty } from '../../../../../../../../common/helpers/object';
/**
 * Trend Time report with filters
 * @param {Function} setIsTimeLoaded - set whether trend time visuals are loaded
 * @returns
 */
const TrendTimeGraphicalWidget = ({ setIsTimeLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const filterList = useSelector(selectTrendTimeFilterList);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const tokenDetails = useSelector(selectEmbedToken);
  const visuals = useSelector(selectTrendTimeVisuals);
  const weeklyVisuals = useSelector(selectWeeklyTrendTimeVisuals);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayTT);
  const occupiedRoomsIsDisplay = useSelector(selectOccupiedRoomsIsDisplayTT);
  const adrIsDisplay = useSelector(selectAdrIsDisplayTT);
  const avgBookingWindowIsDisplay = useSelector(selectAvgBookingWindowIsDisplayTT);
  const avgLosIsDisplay = useSelector(selectAvgLosIsDisplayTT);
  const cxlIsDisplay = useSelector(selectCxlIsDisplayTT);
  const cumulativeVisuals = useSelector(selectTrendTimeCumulativeVisuals);
  const percentageVisuals = useSelector(selectTrendTimePercentageVisuals);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const movingAverageVisuals = useSelector(selectMovingAverageVisualTT);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isInitialTotalRevenueLoaded, setIsInitialTotalRevenueLoaded] = useState(false);
  const [isInitialTotalOccupiedRoomsLoaded, setIsInitialTotalOccupiedRoomsLoaded] = useState(false);
  const [isInitialAdrLoaded, setIsInitialAdrLoaded] = useState(false);
  const [isInitialAvgBookingWindowLoaded, setIsInitialAvgBookingWindowLoaded] = useState(false);
  const [isInitialCancellationsLoaded, setIsInitialCancellationsLoaded] = useState(false);
  const [isInitialAvgLoSLoaded, setIsInitialAvgLoSLoaded] = useState(false);
  //
  const [revenueReport, setRevenueReport] = useState({});
  const [occupiedReport, setOccupiedReport] = useState({});
  const [adrReport, setAdrReport] = useState({});
  const [bookingWindowReport, setBookingWindowReport] = useState({});
  const [stayDailyReport, setStayDailyReport] = useState({});
  const [countDailyReport, setCountDailyReport] = useState({});
  const [isRevenueRendered, setIsRevenueRendered] = useState(false);
  const [isOccupiedRendered, setIsOccupiedRendered] = useState(false);
  const [isAdrRendered, setIsAdrRendered] = useState(false);
  const [isBookingWindowRendered, setIsBookingWindowRendered] = useState(false);
  const [isStayDailyRendered, setIsStayDailyRendered] = useState(false);
  const [isCountDailyRendered, setIsCountDailyRendered] = useState(false);
  const [isV1Processing, setIsV1Processing] = useState(false);
  const [isV2Processing, setIsV2Processing] = useState(false);
  const [isV3Processing, setIsV3Processing] = useState(false);
  const [isV4Processing, setIsV4Processing] = useState(false);
  const [isV5Processing, setIsV5Processing] = useState(false);
  const [isV6Processing, setIsV6Processing] = useState(false);
  // To get visual details
  const getVisual = (index) => {
    let found;
    if (isCumulative) {
      found = findVisual(cumulativeVisuals, index, weeklyViewToggle, [
        REPORT_TYPE.WEEKLY_TREND_TIME,
        REPORT_TYPE.TREND_TIME,
      ]);
    } else if (weeklyViewToggle) {
      found = findVisual(weeklyVisuals, index, true, [REPORT_TYPE.TREND_TIME_CUMULATIVE]);
    } else if (isPercentageChange && !weeklyViewToggle && !isCumulative && !normalizedViewToggle) {
      found = findVisual(percentageVisuals, index, true, [REPORT_TYPE.TREND_TIME_PERCENTAGE], true);
    } else if (isMovingAverage) {
      found = findVisual(movingAverageVisuals, index, true, [
        REPORT_TYPE.TREND_TIME_CUMULATIVE,
        REPORT_TYPE.TREND_TIME_MOVING_AVERAGE,
      ]);
    } else {
      found = findVisual(visuals, index, true, [
        REPORT_TYPE.TREND_TIME_CUMULATIVE,
        REPORT_TYPE.TREND_TIME,
      ]);
    }
    return found;
  };
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = filterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          isPercentage={
            isPercentageChange && !weeklyViewToggle && !isCumulative && !normalizedViewToggle
          }
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(d) => setIsLoaded(d)}
          setIsRendered={(r) => setIsRendered(r)}
          tab={TAB_VALUE.TREND_TIME}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  useEffect(() => {
    // Calculate S based on the given conditions

    const pair1 = revenueIsDisplay ? revenueIsDisplay && isInitialTotalRevenueLoaded : true;
    const pair2 = occupiedRoomsIsDisplay
      ? occupiedRoomsIsDisplay && isInitialTotalOccupiedRoomsLoaded
      : true;
    const pair3 = adrIsDisplay ? adrIsDisplay && isInitialAdrLoaded : true;
    const pair4 = avgBookingWindowIsDisplay
      ? avgBookingWindowIsDisplay && isInitialAvgBookingWindowLoaded
      : true;
    const pair5 = avgLosIsDisplay ? avgLosIsDisplay && isInitialAvgLoSLoaded : true;
    const pair6 = cxlIsDisplay ? cxlIsDisplay && isInitialCancellationsLoaded : true;
    if (
      revenueIsDisplay &&
      isInitialTotalRevenueLoaded &&
      occupiedRoomsIsDisplay &&
      isInitialTotalOccupiedRoomsLoaded &&
      adrIsDisplay &&
      isInitialAdrLoaded &&
      avgBookingWindowIsDisplay &&
      isInitialAvgBookingWindowLoaded &&
      avgLosIsDisplay &&
      isInitialAvgLoSLoaded &&
      cxlIsDisplay &&
      isInitialCancellationsLoaded
    ) {
      setIsTimeLoaded(true);
    } else {
      setIsTimeLoaded(pair1 || pair2 || pair3 || pair4 || pair5 || pair6);
    }
  }, [
    revenueIsDisplay,
    isInitialTotalRevenueLoaded,
    occupiedRoomsIsDisplay,
    isInitialTotalOccupiedRoomsLoaded,
    adrIsDisplay,
    isInitialAdrLoaded,
    avgBookingWindowIsDisplay,
    isInitialAvgBookingWindowLoaded,
    avgLosIsDisplay,
    isInitialAvgLoSLoaded,
    cxlIsDisplay,
    isInitialCancellationsLoaded,
  ]);
  //
  return (
    !isEmpty(filterList) && (
      <>
        <VisualGrid
          isDisplay={revenueIsDisplay}
          visualName={getVisualName(0)}
          visual={getWidget(
            0,
            styles.paceVisual,
            setRevenueReport,
            setIsInitialTotalRevenueLoaded,
            setIsRevenueRendered,
            trendActions.setRevenueIsDisplayTT
          )}
          onClick={() => {
            dispatch(trendActions.setRevenueIsDisplayTT(!revenueIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(0)}
              report={revenueReport}
              isProcessing={isV1Processing}
              setIsProcessing={setIsV1Processing}
              disabled={!isRevenueRendered || isV1Processing || !revenueIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
        />
        <VisualGrid
          isDisplay={occupiedRoomsIsDisplay}
          visualName={getVisualName(1)}
          visual={getWidget(
            1,
            styles.paceVisual,
            setOccupiedReport,
            setIsInitialTotalOccupiedRoomsLoaded,
            setIsOccupiedRendered,
            trendActions.setOccupiedRoomIsDisplayTT
          )}
          onClick={() => {
            dispatch(trendActions.setOccupiedRoomIsDisplayTT(!occupiedRoomsIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(1)}
              report={occupiedReport}
              isProcessing={isV2Processing}
              setIsProcessing={setIsV2Processing}
              disabled={!isOccupiedRendered || isV2Processing || !occupiedRoomsIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
        />
        <VisualGrid
          isDisplay={adrIsDisplay}
          visualName={getVisualName(2)}
          visual={getWidget(
            2,
            styles.paceVisual,
            setAdrReport,
            setIsInitialAdrLoaded,
            setIsAdrRendered,
            trendActions.setAdrIsDisplayTT
          )}
          onClick={() => {
            dispatch(trendActions.setAdrIsDisplayTT(!adrIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(2)}
              report={adrReport}
              isProcessing={isV3Processing}
              setIsProcessing={setIsV3Processing}
              disabled={!isAdrRendered || isV3Processing || !adrIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
        />
        {getWidget(3, styles.paceVisual, () => {}, setIsInitialAvgLoSLoaded) && (
          <VisualGrid
            isDisplay={avgLosIsDisplay}
            visualName={getVisualName(3)}
            visual={getWidget(
              3,
              styles.paceVisual,
              setStayDailyReport,
              setIsInitialAvgLoSLoaded,
              setIsStayDailyRendered,
              trendActions.setAvgLosIsDisplayTT
            )}
            onClick={() => {
              dispatch(trendActions.setAvgLosIsDisplayTT(!avgLosIsDisplay));
            }}
            interpreterFragment={
              <AssistantIcon
                visualName={getVisualName(3)}
                report={stayDailyReport}
                isProcessing={isV4Processing}
                setIsProcessing={setIsV4Processing}
                disabled={!isStayDailyRendered || isV4Processing || !avgLosIsDisplay}
                exportType={exportType}
                setExportType={setExportType}
              />
            }
          />
        )}
        {getWidget(4, styles.paceVisual, () => {}, setIsInitialAvgBookingWindowLoaded) && (
          <VisualGrid
            isDisplay={avgBookingWindowIsDisplay}
            visualName={getVisualName(4)}
            visual={getWidget(
              4,
              styles.paceVisual,
              setBookingWindowReport,
              setIsInitialAvgBookingWindowLoaded,
              setIsBookingWindowRendered,
              trendActions.setAvgBookingWindowIsDisplayTT
            )}
            onClick={() => {
              dispatch(trendActions.setAvgBookingWindowIsDisplayTT(!avgBookingWindowIsDisplay));
            }}
            interpreterFragment={
              <AssistantIcon
                visualName={getVisualName(4)}
                report={bookingWindowReport}
                isProcessing={isV5Processing}
                setIsProcessing={setIsV5Processing}
                disabled={!isBookingWindowRendered || isV5Processing || !avgBookingWindowIsDisplay}
                exportType={exportType}
                setExportType={setExportType}
              />
            }
          />
        )}
        {getWidget(5, styles.paceVisual, () => {}, setIsInitialCancellationsLoaded) && (
          <VisualGrid
            isDisplay={cxlIsDisplay}
            visualName={getVisualName(5)}
            visual={getWidget(
              5,
              styles.paceVisual,
              setCountDailyReport,
              setIsInitialCancellationsLoaded,
              setIsCountDailyRendered,
              trendActions.setCxlIsDisplayTT
            )}
            onClick={() => {
              dispatch(trendActions.setCxlIsDisplayTT(!cxlIsDisplay));
            }}
            interpreterFragment={
              <AssistantIcon
                visualName={getVisualName(5)}
                report={countDailyReport}
                isProcessing={isV6Processing}
                setIsProcessing={setIsV6Processing}
                disabled={!isCountDailyRendered || isV6Processing || !cxlIsDisplay}
                exportType={exportType}
                setExportType={setExportType}
              />
            }
          />
        )}
      </>
    )
  );
};
//
export default TrendTimeGraphicalWidget;
